<template>
  <section class="ma-4 ma-md-8">
    <t-floating-bar v-if="eventFilter.resource?.value">
      {{ eventName(eventFilter.resource.value!) }}
    </t-floating-bar>
    <t-page-title title="Vendas | Relatório de Faturamento">
      <template #append-custom-action>
        <v-card
          v-tooltip="'A visualização simplificada mostra apenas os lotes, quantidades e valores dos ingresso por Ponto de Venda.'"
          class="px-3"
        >
          <v-switch
            v-model="simplified"
            label="Visualização Simplificada"
            color="primary"
            hide-details
          />
        </v-card>
      </template>
    </t-page-title>
    <v-form class="mb-8" @submit.prevent="fetch">
      <v-row>
        <v-col
          v-for="filter in filters"
          :key="filter.field"
          cols="12"
          md="3"
        >
          <data-table-select-filter
            v-if="filter.type == 'select'"
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />
          <data-table-date-interval-filter
            v-if="filter.type == 'date-interval'"
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            :disabled="loading"
            size="large"
            block
            type="submit"
            class="no-print"
          >
            Filtrar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <event-revenue-overview v-if="!simplified" :items="items" />
    <v-card
      v-for="itemGroup in groupBy<RevenueRecord, string>(items, (a) => a.pos)"
      :key="itemGroup[0].pos"
      class="mb-6 pa-4"
      :title="itemGroup[0].pos"
    >
      <template v-if="simplified">
        <event-revenue-item-simple-table :items="itemGroup" :loading="loading" />
      </template>
      <template v-else>
        <div class="mt-8 ps-4 text-subtitle-1 font-weight-bold">
          Ingressos
        </div>
        <event-revenue-item-table :items="itemGroup.filter(t => t.type !== 'Cortesia')" :loading="loading" />

        <div class="mt-8 ps-4 text-subtitle-1 font-weight-bold">
          Cortesias
        </div>
        <event-revenue-item-table :items="itemGroup.filter(t => t.type === 'Cortesia')" :loading="loading" />
      </template>
    </v-card>
    <v-card v-if="error" class="pa-4 my-3 text-center">
      Não há dados disponíveis
    </v-card>
  </section>
</template>

<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import EventService from '@/services/EventService'
import DataTableSelectFilter from '@/components/datatable/DataTableSelectFilter.vue'
import type RevenueRecord from '@/types/RevenueRecord'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useProducerStore } from '@/stores/producer'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { groupBy } from '@/helpers/array'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import DataTableDateIntervalFilter from '@/components/datatable/DataTableDateIntervalFilter.vue'
import EventRevenueItemTable from './partials/event-revenue-item-table.vue'
import EventRevenueItemSimpleTable from './partials/event-revenue-item-simple-table.vue'
import { useRoute } from 'vue-router'
import EventRevenueOverview from './partials/event-revenue-overview.vue'
import { eventName } from '@/plugins/eventName'

const loader = useLoader()
const snackbar = useSnackBar()

const error = ref(false)
const items: Ref<RevenueRecord[]> = ref([])

const simplified = ref(false)

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
  initial: useRoute().params.event !== '' ? useRoute().params.event as string : null,
})

const filters = ref([
  eventFilter,
  useDateIntervalFilter({
    label: 'Período',
    field: 'date',
    required: false,
    initial: null,
  }),
])

const loading = computed(() => loader.loading)

async function fetch() {
  try {
    loader.increment()

    await Promise.all(filters.value.map(f => f.ready))

    const eventFilter = filters.value.find(f => f.field === 'event_id')!
    const id = eventFilter.value!

    const appliedFilters = filters.value.filter(f => f.model !== null).map(
      f => `${f.field}=${f.value as unknown as string}`,
    ).join('&')

    const response = await new EventService().revenue(id, appliedFilters)

    items.value = response.data.data.toSorted(
      (a, b) => {
        if (a.pos === 'Venda Online') return -1

        return a.pos.localeCompare(b.pos)
      },
    )
  } catch {
    snackbar.show('Não foi possível buscar as informações', 'error')
    error.value = true
  } finally {
    loader.decrement()
  }
}

onMounted(() => fetch())

</script>
